/**
 * Decking, drawn in each bay's local frame (X along a→b, Z along a→c).
 *
 * Planks are 90 mm ALP profiles with an anti-slip strip and hooks over the
 * transoms. A hatch bay gets the 690 mm Lukeplan strip with an open lid and a
 * Ladder ST 2100 hanging in the opening (T-2091210). Planks crossing a stair
 * opening are split into the runs that remain clear.
 */

import { Group, Mesh } from "three";
import { ALP_LENGTHS, CFG, MM, nodeY } from "../../data/haki.ts";
import { bayFrame, dist, nearestOf, openRuns } from "../../model/geometry.ts";
import { boardLength, stairCovers } from "../../model/operations.ts";
import { packPlanks } from "../../model/solver.ts";
import type { Deck } from "../../model/types.ts";
import { G } from "../geometries.ts";
import { M } from "../materials.ts";
import { boxAt, tubeBetween, V, yawOf } from "../threeHelpers.ts";
import { DECK_LIFT, PLANK_THICKNESS, type RenderContext } from "./context.ts";

/** Shortest plank run worth drawing, metres. */
const MIN_PLANK_RUN = 0.45;

/** How far the ladder foot stands out from the hatch opening, metres. */
const LADDER_LEAN = 0.6;

type LocalBox = (
  cx: number,
  cy: number,
  cz: number,
  sx: number,
  sy: number,
  sz: number,
  material: (typeof M)[keyof typeof M],
) => Mesh;

/** The hatch strip, its lid, and the ladder hanging in the opening. */
function renderHatch(
  ctx: RenderContext,
  deck: Deck,
  idx: number,
  group: Group,
  box: LocalBox,
  frame: ReturnType<typeof bayFrame>,
  y: number,
): number {
  const art = CFG.hatch.art[deck.len];
  const kg = CFG.hatch.kg[deck.len];
  const hatchWidth = CFG.hatch.width * MM;
  const side = deck.hatchSide ?? 0;
  const end = deck.hatchEnd ?? 1;
  const stripZ = side ? frame.W - 0.045 - hatchWidth : 0.045;
  const local = (z: number) => frame.sgn * z;

  const strip = box(
    frame.L / 2,
    y,
    stripZ + hatchWidth / 2,
    frame.L - 0.13,
    PLANK_THICKNESS,
    hatchWidth,
    M.alu,
  );
  strip.userData = { type: "deck", idx };

  const lidX = end ? frame.L - 0.55 : 0.55;
  const direction = end ? 1 : -1;
  box(
    frame.L / 2 - direction * 0.35,
    y + PLANK_THICKNESS / 2 + 0.004,
    stripZ + hatchWidth / 2,
    frame.L - 1.2,
    0.008,
    hatchWidth - 0.14,
    M.grip,
  );
  box(lidX, y + PLANK_THICKNESS / 2 + 0.05, stripZ + hatchWidth / 2 - 0.32, 0.62, 0.1, 0.02, M.hatchLid);
  box(
    lidX,
    y + PLANK_THICKNESS / 2 + 0.006,
    stripZ + hatchWidth / 2 + 0.15,
    0.62,
    0.012,
    hatchWidth - 0.35,
    M.hatchLid,
  );

  if (art !== undefined && kg !== undefined) {
    ctx.bom.add("Decking", `Hatch deck AL 690×${deck.len}`, art, kg);
  }

  const baseY = deck.p >= 4 ? nodeY(deck.p - 4) + DECK_LIFT + PLANK_THICKNESS : nodeY(0);
  const centreZ = stripZ + hatchWidth / 2;
  const halfWidth = (CFG.ladder.width * MM) / 2;

  /* The ladder climbs along the bay's LONG axis, leaning from the hatch opening
     back toward the middle of the bay. The 600 mm lean over a 2.0 m lift is the
     ST 2100's own geometry (√(2.0² + 0.6²) ≈ 2.09 m against its 2145 mm length);
     the bay's width has no room for it. */
  const lean = (end ? -1 : 1) * LADDER_LEAN;
  const footX = lidX + lean;
  const headX = lidX - lean * 0.08;
  const hookDx = -Math.sign(lean) * 0.06;

  for (const edge of [-halfWidth, halfWidth]) {
    group.add(
      tubeBetween(
        V(footX, baseY + 0.05, local(centreZ + edge)),
        V(headX, y + 0.02, local(centreZ + edge)),
        G.tube27,
        M.aluD,
      ),
    );
  }

  const rungs = Math.floor((y - baseY) / (CFG.ladder.rung * MM));
  for (let step = 1; step <= rungs; step++) {
    const t = step / (rungs + 0.5);
    const rungX = footX + (headX - footX) * t;
    const rungY = baseY + 0.05 + (y + 0.02 - (baseY + 0.05)) * t;
    group.add(
      tubeBetween(
        V(rungX, rungY, local(centreZ - halfWidth)),
        V(rungX, rungY, local(centreZ + halfWidth)),
        G.tube27,
        M.aluD,
      ),
    );
  }

  for (const edge of [-halfWidth, halfWidth]) {
    group.add(
      tubeBetween(
        V(headX, y + 0.02, local(centreZ + edge)),
        V(headX + hookDx, y + 0.08, local(centreZ + edge)),
        G.tube27,
        M.cast,
      ),
    );
  }

  ctx.bom.add("Access", CFG.ladder.name, CFG.ladder.art, CFG.ladder.kg);

  return side ? 0.045 : stripZ + hatchWidth + 0.006;
}

function renderPlanks(
  ctx: RenderContext,
  deck: Deck,
  idx: number,
  box: LocalBox,
  frame: ReturnType<typeof bayFrame>,
  y: number,
  widths: number[],
  startZ: number,
): void {
  let z = startZ;

  widths.forEach((widthMm, slot) => {
    const plankWidth = (widthMm - 10) * MM;
    const centreZ = z + (widthMm * MM) / 2;
    const off = deck.removed.has(slot);
    const runs = openRuns(0.065, frame.L - 0.065, (x) =>
      stairCovers(ctx.model, frame.toWorld(x, centreZ), deck.p, CFG.utv.width),
    );

    for (const [from, to] of runs) {
      const runLength = to - from;
      if (runLength < MIN_PLANK_RUN) {
        continue;
      }

      const plank = box(
        (from + to) / 2,
        y,
        centreZ,
        runLength - 0.02,
        PLANK_THICKNESS,
        plankWidth,
        off ? M.deckOff : M.alu,
      );
      plank.userData = { type: "plank", idx, slot, fam: widthMm };
      plank.castShadow = !off;
      plank.receiveShadow = !off;
      ctx.pickPlanks.push(plank);

      if (off) {
        continue;
      }

      box(
        (from + to) / 2,
        y + PLANK_THICKNESS / 2 + 0.004,
        centreZ,
        runLength - 0.09,
        0.008,
        plankWidth - 0.02,
        M.grip,
      );
      for (const edge of [1, -1]) {
        box(
          (from + to) / 2 + edge * (runLength / 2 - 0.05),
          y - PLANK_THICKNESS / 2 + 0.02,
          centreZ,
          0.07,
          0.06,
          plankWidth - 0.03,
          M.cast,
        );
      }

      const plankLength = nearestOf(ALP_LENGTHS, runLength * 1000 + 120);
      const family = widthMm === 170 ? CFG.alp[170] : CFG.alp[230];
      ctx.bom.add(
        "Decking",
        `AL plank ALP ${widthMm}×${plankLength}`,
        family.art[plankLength] ?? "—",
        family.kg[plankLength] ?? 5,
      );
    }

    z += widthMm * MM;
  });
}

export function renderDecks(ctx: RenderContext): void {
  ctx.model.decks.forEach((deck, idx) => {
    const frame = bayFrame(deck.c);
    const group = new Group();
    group.position.set(frame.a.x, 0, frame.a.z);
    group.rotation.y = frame.rotY;

    const y = nodeY(deck.p) + DECK_LIFT + PLANK_THICKNESS / 2;
    const box: LocalBox = (cx, cy, cz, sx, sy, sz, material) => {
      const mesh = boxAt(cx, cy, frame.sgn * cz, sx, sy, sz, material, 0);
      group.add(mesh);
      return mesh;
    };

    const hasHatch = deck.hatch && CFG.hatch.art[deck.len] !== undefined;
    const family = deck.fam ?? ctx.plankFam;
    let widths: number[];
    let startZ: number;

    if (hasHatch) {
      widths = packPlanks(deck.wid - CFG.hatch.width, family);
      startZ = renderHatch(ctx, deck, idx, group, box, frame, y);
    } else {
      widths = packPlanks(deck.wid, family);
      const covered = widths.reduce((total, w) => total + w, 0);
      startZ = 0.045 + ((deck.wid - 90 - covered) * MM) / 2;
    }

    renderPlanks(ctx, deck, idx, box, frame, y, widths, startZ);

    for (const child of group.children) {
      if (!child.userData.type) {
        child.userData = { type: "deck", idx };
      }
      if (child instanceof Mesh) {
        ctx.pickElems.push(child);
      }
    }
    ctx.root.add(group);
  });
}

/**
 * Loose boards: one ALP each, hooked over the two edges it spans. Drawn like a
 * deck plank — profile, anti-slip strip, end hooks — in a frame along a→b.
 */
export function renderBoards(ctx: RenderContext): void {
  ctx.model.boards.forEach((board, idx) => {
    const length = dist(board.a, board.b);
    const group = new Group();
    group.position.set(board.a.x, 0, board.a.z);
    group.rotation.y = yawOf(board.b.x - board.a.x, board.b.z - board.a.z) - Math.PI / 2;

    const y = nodeY(board.p) + DECK_LIFT + PLANK_THICKNESS / 2;
    const plankWidth = (board.wid - 10) * MM;
    const run = length - 0.13;

    group.add(boxAt(length / 2, y, 0, run - 0.02, PLANK_THICKNESS, plankWidth, M.alu, 0));
    group.add(
      boxAt(length / 2, y + PLANK_THICKNESS / 2 + 0.004, 0, run - 0.09, 0.008, plankWidth - 0.02, M.grip, 0),
    );
    for (const edge of [1, -1]) {
      group.add(
        boxAt(
          length / 2 + edge * (run / 2 - 0.05),
          y - PLANK_THICKNESS / 2 + 0.02,
          0,
          0.07,
          0.06,
          plankWidth - 0.03,
          M.cast,
          0,
        ),
      );
    }

    for (const child of group.children) {
      child.userData = { type: "board", idx };
      if (child instanceof Mesh) {
        ctx.pickElems.push(child);
      }
    }
    ctx.root.add(group);

    const plankLength = boardLength(board);
    const family = board.wid === 170 ? CFG.alp[170] : CFG.alp[230];
    ctx.bom.add(
      "Decking",
      `AL plank ALP ${board.wid}×${plankLength}`,
      family.art[plankLength] ?? "—",
      family.kg[plankLength] ?? 5,
    );
  });
}
